import type { GetterOptions, SetterOptions } from "DependencyOptions";
import { getAvailableResult, getNullAvailableResult } from "DependencyResult";
import DependencyV2Visitor, {
  DependencyType,
  type ParsedDependency,
  type PropertyValueFunction,
} from "DependencyV2Visitor";
import type { DependencyAccessMixedValue, DependencyVisitorContext } from "DependencyVisitorContext";
import { getInterfaceName, isEntity } from "EntityExtensions";
import { isDataProperty, isNavigationProperty } from "EntityMetadataExtensions";
import { hasPropertyChanged } from "EntityPropertyExtensions";
import { DependencyError, DependencyOptionsError } from "Errors";
import type { Arbitrary_funcContext } from "wtg-expressions/lib/ExpressionParser";
import pathEvaluator, { getPropertyValueForItem } from "../DependencyV2Visitor/PathEvaluator.ts";
import { arbitraryFunc } from "./SecurityFunctions.ts";

export default class SecurityExpressionVisitor extends DependencyV2Visitor {
  protected static override _validateOptions(
    _strategy: unknown,
    options: GetterOptions | SetterOptions,
    parsed: ParsedDependency,
  ): void {
    if (parsed.dependencyType === DependencyType.MacroPath) {
      throw new DependencyOptionsError("Does not support a macro path.");
    } else if ("valueToSet" in options) {
      throw new DependencyOptionsError("Does not support setting values.");
    } else if (options.skipLastSegmentEvaluation) {
      throw new DependencyOptionsError("Does not support skipLastSegmentEvaluation.");
    }
  }

  protected override _getPropertyValueFunction<Mode>(): PropertyValueFunction<Mode> {
    return getValueForItem;
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  override visitArbitrary_func(ctx: Arbitrary_funcContext): void {
    const fnName = ctx.getChild(0).getText();
    this._visitFunction(ctx, arbitraryFunc.bind(undefined, fnName), undefined);
  }
}

function getValueForItem<Mode>(
  propertyName: string,
  separator: string | undefined,
  isTopLevel: boolean,
  data: unknown,
  context: DependencyVisitorContext<Mode>,
): DependencyAccessMixedValue<Mode, unknown> {
  if (data == null) {
    return getNullAvailableResult();
  }

  if (Array.isArray(data)) {
    throw new DependencyError("Data is a collection, which is not supported.");
  }

  if (!isEntity(data)) {
    throw new DependencyError(`Property '${propertyName}' cannot be used because the owner is not an entity.`);
  }

  const property = data.entityType.getProperty(propertyName);
  if (!property) {
    throw new DependencyError(`Property '${getInterfaceName(data)}.${propertyName}' does not exist.`);
  }

  if (isNavigationProperty(property)) {
    if (!property.isScalar) {
      throw new DependencyError(`Navigation property '${propertyName}' is a collection, which is not supported.`);
    }
    if (!data.entityAspect.entityState.isAdded() && hasPropertyChanged(data, property.foreignKeyNames[0])) {
      throw new DependencyError(`Foreign key for '${getInterfaceName(data)}.${propertyName}' cannot be changed.`);
    }
  }

  if (isDataProperty(property)) {
    if (data.entityAspect.entityState.isAdded()) {
      return getAvailableResult(property.defaultValue);
    } else {
      return pathEvaluator.originalValue(propertyName, data, context);
    }
  } else {
    return getPropertyValueForItem(propertyName, separator, isTopLevel, data, context);
  }
}
